import { decimalText, nonnegativeDecimalSchema, positiveDecimalSchema } from "../values/decimal";
import { isLifecycleDateRequired, type LifecycleDateField } from "./order-lifecycle-policy";
import { isOrderDirection, isOrderStatus, ORDER_DIRECTION_VALUES, ORDER_DIRECTIONS, ORDER_STATUS_VALUES, ORDER_STATUSES, type OrderDirection, type OrderStatus } from "./order-types";
import { UNITS } from "../inventory/units";
import { buildCsv, UTF8_BOM } from "../values/csv";
import { parseDateOnly, todayDateOnly } from "../values/date-only";
import {
  importContactMatchIssue,
  importDateOptionalGuidance,
  importDateRequiredAllGuidance,
  importDateRequiredForGuidance,
  importExpectedColumnsIssue,
  importPrecedesIssue,
  importRequiredForStatusIssue,
  importUseOneOfIssue,
  ORDER_IMPORT_CONTACT_PARTY,
  ORDER_IMPORT_DATE_LABELS,
  ORDER_IMPORT_EXAMPLES,
  ORDER_IMPORT_FILE_ISSUES,
  ORDER_IMPORT_ISSUES,
  ORDER_IMPORT_REQUESTOR_COPY,
  ORDER_IMPORT_ROW_GUIDANCE_TEXT,
} from "./order-import-copy";
export const ORDER_IMPORT_CONTRACT_VERSION = "orders-v1" as const;

const IMPORT_DATE_FIELDS = [
  ["entered_on", "submittedAt", ORDER_IMPORT_DATE_LABELS.enteredOn],
  ["ordered_on", "orderedAt", ORDER_IMPORT_DATE_LABELS.orderedOn],
  ["fulfilled_on", "arrivedDate", ORDER_IMPORT_DATE_LABELS.fulfilledOn],
] as const satisfies ReadonlyArray<readonly [OrderImportHeader, LifecycleDateField, string]>;

export const ORDER_IMPORT_HEADERS = [
  "direction",
  "contact_name",
  "chemical_name",
  "cas_number",
  "quantity",
  "unit",
  "status",
  "entered_on",
  "ordered_on",
  "fulfilled_on",
  "requested_by_email",
  "price_per_item",
  "reference_number",
  "notes",
] as const;

export type OrderImportHeader = typeof ORDER_IMPORT_HEADERS[number];
export type OrderImportRow = Record<OrderImportHeader, string>;
export type OrderImportIssue = { row: number; field: OrderImportHeader; message: string };

const IMPORTABLE_STATUS = {
  requested: true,
  ordered: true,
  fulfilled: true,
  cancelled: false,
} as const satisfies Record<OrderStatus, boolean>;

export type OrderImportStatus = {
  [Status in OrderStatus]: (typeof IMPORTABLE_STATUS)[Status] extends true ? Status : never;
}[OrderStatus];

function isImportableStatus(status: OrderStatus): status is OrderImportStatus {
  return IMPORTABLE_STATUS[status];
}

export const ORDER_IMPORT_STATUSES = ORDER_STATUS_VALUES.filter(isImportableStatus);
export const ORDER_IMPORT_DIRECTIONS = ORDER_DIRECTION_VALUES;

const REQUESTOR_EMAIL_POLICY = {
  purchase: { required: true, ...ORDER_IMPORT_REQUESTOR_COPY.purchase },
  sale: { required: false, ...ORDER_IMPORT_REQUESTOR_COPY.sale },
} as const satisfies Record<OrderDirection, { required: boolean; issue: string; guide: string }>;

export const ORDER_IMPORT_REQUESTOR_GUIDANCE = `${REQUESTOR_EMAIL_POLICY.purchase.guide}. ${REQUESTOR_EMAIL_POLICY.sale.guide}.`;

function requiredImportStatuses(field: LifecycleDateField): OrderImportStatus[] {
  const required: OrderImportStatus[] = [];
  for (const status of ORDER_IMPORT_STATUSES) {
    if (isLifecycleDateRequired(field, status)) required.push(status);
  }
  return required;
}

function dateGuidance([, field, label]: typeof IMPORT_DATE_FIELDS[number]): string {
  const required = requiredImportStatuses(field);
  if (required.length === 0) return importDateOptionalGuidance(label);
  if (required.length === ORDER_IMPORT_STATUSES.length) return importDateRequiredAllGuidance(label);
  return importDateRequiredForGuidance(label, required.join(" and "));
}

export const ORDER_IMPORT_DATE_GUIDANCE = `${IMPORT_DATE_FIELDS.map(dateGuidance).join(". ")}.`;
export const ORDER_IMPORT_UNITS = UNITS;
export const ORDER_IMPORT_TEMPLATE_FILENAME = "chem-inventory-orders-v1.csv";
export const ORDER_IMPORT_EXAMPLE_FILENAME = "chem-inventory-orders-v1-example.csv";

export const ORDER_IMPORT_REQUIRED_HEADERS = [
  "direction", "contact_name", "chemical_name", "quantity", "unit", "status", "entered_on",
] as const satisfies readonly OrderImportHeader[];
export const ORDER_IMPORT_OPTIONAL_HEADERS = [
  "cas_number", "ordered_on", "fulfilled_on", "requested_by_email", "price_per_item", "reference_number", "notes",
] as const satisfies readonly OrderImportHeader[];

export const ORDER_IMPORT_ROW_GUIDANCE = ORDER_IMPORT_ROW_GUIDANCE_TEXT;
const MULTI_QUANTITY = /^[+-]?\d+(?:\.\d+)?\s*(?:[;|\n]\s*[+-]?\d+(?:\.\d+)?\s*)+$/;

export function orderImportQuantityIssue(value: string): string | null {
  if (MULTI_QUANTITY.test(value.trim())) return ORDER_IMPORT_ROW_GUIDANCE;
  return PLAIN_DECIMAL.test(value) && positiveDecimalSchema.safeParse(value).success ? null : ORDER_IMPORT_ISSUES.positiveDecimal;
}

export function buildOrderImportTemplateCsv(): string {
  return buildCsv([ORDER_IMPORT_HEADERS]);
}

export function buildOrderImportExampleCsv(): string {
  const { purchase, sale } = ORDER_IMPORT_EXAMPLES;
  const examples = [
    [ORDER_DIRECTIONS.purchase, purchase.contactName, purchase.chemicalName, "123-45-6", "2.5", "L", ORDER_STATUSES.requested, "2025-01-15", "", "", "buyer@example.com", "12.50", "PO-EXAMPLE-001", purchase.notes],
    [ORDER_DIRECTIONS.sale, sale.contactName, sale.chemicalName, "654-32-1", "1", "kg", ORDER_STATUSES.fulfilled, "2025-01-15", "2025-01-16", "2025-01-17", "", "25.00", "SO-EXAMPLE-001", sale.notes],
  ];
  return buildCsv([ORDER_IMPORT_HEADERS, ...examples]);
}

function parseCsvRecords(csv: string): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let cell = "";
  let quoted = false;
  const source = csv.startsWith(UTF8_BOM) ? csv.slice(1) : csv;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    if (quoted) {
      if (character === '"' && source[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else if (character === '"') {
        quoted = false;
      } else {
        cell += character;
      }
      continue;
    }
    if (character === '"' && cell.length === 0) quoted = true;
    else if (character === ",") { record.push(cell); cell = ""; }
    else if (character === "\n") {
      record.push(cell.replace(/\r$/, ""));
      if (record.some((value) => value.length > 0)) records.push(record);
      record = [];
      cell = "";
    } else cell += character;
  }
  if (cell.length > 0 || record.length > 0) {
    record.push(cell.replace(/\r$/, ""));
    if (record.some((value) => value.length > 0)) records.push(record);
  }
  return records;
}

function hasBalancedCsvQuotes(csv: string): boolean {
  let quoted = false;
  for (let index = 0; index < csv.length; index += 1) {
    if (csv[index] !== '"') continue;
    if (quoted && csv[index + 1] === '"') index += 1;
    else quoted = !quoted;
  }
  return !quoted;
}

function issue(row: number, field: OrderImportHeader, message: string): OrderImportIssue {
  return { row, field, message };
}

function validateRow(row: OrderImportRow, rowNumber: number): OrderImportIssue[] {
  const issues: OrderImportIssue[] = [];
  for (const field of ["direction", "contact_name", "chemical_name", "unit", "entered_on"] as const) {
    if (!row[field].trim()) issues.push(issue(rowNumber, field, ORDER_IMPORT_ISSUES.required));
  }
  const quantityIssue = orderImportQuantityIssue(row.quantity);
  if (quantityIssue) issues.push(issue(rowNumber, "quantity", quantityIssue));
  if (!ORDER_IMPORT_STATUSES.includes(row.status as OrderImportStatus)) {
    issues.push(issue(rowNumber, "status", importUseOneOfIssue(ORDER_IMPORT_STATUSES.join(", "))));
  }
  if (!isOrderDirection(row.direction)) {
    issues.push(issue(rowNumber, "direction", importUseOneOfIssue(ORDER_IMPORT_DIRECTIONS.join(", "))));
  } else {
    const policy = REQUESTOR_EMAIL_POLICY[row.direction];
    if (policy.required !== Boolean(row.requested_by_email.trim())) {
      issues.push(issue(rowNumber, "requested_by_email", policy.issue));
    }
  }
  if (isOrderStatus(row.status)) {
    for (const [header, field] of IMPORT_DATE_FIELDS) {
      if (!row[header].trim() && isLifecycleDateRequired(field, row.status)) {
        issues.push(issue(rowNumber, header, importRequiredForStatusIssue(row.status)));
      }
    }
  }
  return issues;
}

export type ParseOrderImportResult =
  | { success: true; rows: OrderImportRow[] }
  | { success: false; code: "invalid_headers" | "invalid_rows"; issues: OrderImportIssue[]; rows?: OrderImportRow[] };

export function parseOrderImportCsv(csv: string): ParseOrderImportResult {
  const records = parseCsvRecords(csv);
  const headers = records[0] ?? [];
  const exactHeaders = headers.length === ORDER_IMPORT_HEADERS.length
    && headers.every((header, index) => header === ORDER_IMPORT_HEADERS[index]);
  if (!exactHeaders) return { success: false, code: "invalid_headers", issues: [] };

  const malformedRow = records.slice(1).findIndex((values) => values.length !== ORDER_IMPORT_HEADERS.length);
  if (malformedRow >= 0) {
    return {
      success: false,
      code: "invalid_rows",
      issues: [issue(malformedRow + 2, "notes", importExpectedColumnsIssue(ORDER_IMPORT_HEADERS.length))],
    };
  }

  const rows = records.slice(1).map((values) => Object.assign(
    Object.fromEntries(ORDER_IMPORT_HEADERS.map((header, index) => [header, values[index]?.trim() ?? ""])),
  ) as OrderImportRow);
  const issues = rows.flatMap((row, index) => validateRow(row, index + 2));
  return issues.length > 0
    ? { success: false, code: "invalid_rows", issues, rows }
    : { success: true, rows };
}

type ImportReference = { id: number };
export type OrderImportPreviewReferences = {
  suppliers: readonly (ImportReference & { name: string })[];
  purchasers: readonly (ImportReference & { name: string })[];
  users: readonly (ImportReference & { email: string })[];
  chemicals: readonly (ImportReference & { name: string; casNumber: string })[];
};

export type ProposedOrderImport = {
  row: number;
  direction: OrderDirection;
  contactId: number;
  chemicalId: number | null;
  chemicalName: string;
  casNumber: string | null;
  quantity: string;
  unit: string;
  orderStatus: OrderImportStatus;
  submittedAt: string;
  orderedAt: string | null;
  arrivedDate: string | null;
  requestedBy: number | null;
  pricePerItem: string | null;
  referenceNumber: string | null;
  notes: string | null;
};

export type OrderImportPreview = {
  valid: boolean;
  fileIssue?: string;
  issues: OrderImportIssue[];
  proposedOrders: ProposedOrderImport[];
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const PLAIN_DECIMAL = /^\d+(?:\.\d+)?$/;

function validIsoDate(value: string): boolean {
  return ISO_DATE.test(value) && parseDateOnly(value) !== null;
}

function uniqueMatch<T>(values: readonly T[], predicate: (value: T) => boolean): T | undefined {
  const matches = values.filter(predicate);
  return matches.length === 1 ? matches[0] : undefined;
}

function appendImportIssue(issues: OrderImportIssue[], row: number, field: OrderImportHeader, message: string): void {
  for (const existing of issues) {
    if (existing.row === row && existing.field === field) return;
  }
  issues.push(issue(row, field, message));
}

function contactParty(direction: string): string {
  return direction === ORDER_DIRECTIONS.sale ? ORDER_IMPORT_CONTACT_PARTY.sale : ORDER_IMPORT_CONTACT_PARTY.purchase;
}

export function previewOrderImport(csv: string, references: OrderImportPreviewReferences): OrderImportPreview {
  if (!hasBalancedCsvQuotes(csv)) {
    return { valid: false, fileIssue: ORDER_IMPORT_FILE_ISSUES.unclosedQuote, issues: [], proposedOrders: [] };
  }
  const parsed = parseOrderImportCsv(csv);
  if (!parsed.success && !parsed.rows) {
    return {
      valid: false,
      fileIssue: parsed.code === "invalid_headers" ? ORDER_IMPORT_FILE_ISSUES.headers : undefined,
      issues: parsed.issues,
      proposedOrders: [],
    };
  }
  const rows = parsed.rows ?? [];
  if (rows.length === 0) {
    return { valid: false, fileIssue: ORDER_IMPORT_FILE_ISSUES.noRows, issues: [], proposedOrders: [] };
  }

  const issues: OrderImportIssue[] = parsed.success ? [] : [...parsed.issues];
  const proposedOrders: ProposedOrderImport[] = [];
  for (const [index, row] of rows.entries()) {
    const rowNumber = index + 2;
    const add = appendImportIssue.bind(null, issues, rowNumber);
    for (const field of ["entered_on", "ordered_on", "fulfilled_on"] as const) {
      if (row[field] && !validIsoDate(row[field])) add(field, ORDER_IMPORT_ISSUES.realDate);
      else if (row[field] && row[field] > todayDateOnly()) add(field, ORDER_IMPORT_ISSUES.future);
    }

    if (row.price_per_item && (!PLAIN_DECIMAL.test(row.price_per_item) || !nonnegativeDecimalSchema.safeParse(row.price_per_item).success)) add("price_per_item", ORDER_IMPORT_ISSUES.plainDecimal);
    if (!ORDER_IMPORT_UNITS.includes(row.unit as typeof ORDER_IMPORT_UNITS[number])) add("unit", importUseOneOfIssue(ORDER_IMPORT_UNITS.join(", ")));
    if (row.requested_by_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.requested_by_email)) add("requested_by_email", ORDER_IMPORT_ISSUES.validEmail);
    if (validIsoDate(row.entered_on) && validIsoDate(row.ordered_on) && row.ordered_on < row.entered_on) add("ordered_on", importPrecedesIssue(ORDER_IMPORT_DATE_LABELS.enteredOn));
    if (validIsoDate(row.ordered_on) && validIsoDate(row.fulfilled_on) && row.fulfilled_on < row.ordered_on) add("fulfilled_on", importPrecedesIssue(ORDER_IMPORT_DATE_LABELS.orderedOn));
    if (validIsoDate(row.entered_on) && validIsoDate(row.fulfilled_on) && row.fulfilled_on < row.entered_on) add("fulfilled_on", importPrecedesIssue(ORDER_IMPORT_DATE_LABELS.enteredOn));

    const contacts = row.direction === ORDER_DIRECTIONS.purchase ? references.suppliers : references.purchasers;
    const contact = uniqueMatch(contacts, (value) => value.name === row.contact_name);
    if (!contact) add("contact_name", importContactMatchIssue(contactParty(row.direction)));
    const requestedBy = row.direction === ORDER_DIRECTIONS.purchase
      ? uniqueMatch(references.users, (value) => value.email.toLowerCase() === row.requested_by_email.toLowerCase())
      : undefined;
    if (row.direction === ORDER_DIRECTIONS.purchase && !requestedBy) add("requested_by_email", ORDER_IMPORT_ISSUES.requestorMatch);

    const nameMatches = references.chemicals.filter((value) => value.name === row.chemical_name);
    const casMatches = row.cas_number ? references.chemicals.filter((value) => value.casNumber === row.cas_number) : [];
    let chemicalId: number | null = null;
    if (nameMatches.length > 1 || casMatches.length > 1) add("chemical_name", ORDER_IMPORT_ISSUES.chemicalAtMostOne);
    else if (nameMatches.length === 1 && casMatches.length === 1 && nameMatches[0].id !== casMatches[0].id) add("cas_number", ORDER_IMPORT_ISSUES.chemicalCasMismatch);
    else chemicalId = nameMatches[0]?.id ?? casMatches[0]?.id ?? null;

    if (issues.some((value) => value.row === rowNumber)) continue;
    proposedOrders.push({
      row: rowNumber, direction: row.direction as "purchase" | "sale", contactId: contact!.id, chemicalId,
      chemicalName: row.chemical_name, casNumber: row.cas_number || null,
      quantity: decimalText(row.quantity), unit: row.unit, orderStatus: row.status as OrderImportStatus,
      submittedAt: row.entered_on, orderedAt: row.ordered_on || null, arrivedDate: row.fulfilled_on || null,
      requestedBy: requestedBy?.id ?? null, pricePerItem: row.price_per_item || null,
      referenceNumber: row.reference_number || null, notes: row.notes || null,
    });
  }
  return { valid: issues.length === 0, issues, proposedOrders: issues.length === 0 ? proposedOrders : [] };
}
